import { useEffect, useState } from 'react';
import { defaultRatePct, PRIME } from '../../core/estimate.js';
import { coverage } from '../../core/financials.js';
import { monthlyPayment, totalInterest, rateCeilingPct } from '../../core/rates.js';
import { fmtMoney, parseAmount } from '../../intake/parseAnswer.js';
import { newId, type Scenario } from '../../workspace/schema.js';
import { PageHead, Term } from '../components.js';
import { useWorkspace } from '../state.js';

const TERMS = [60, 84, 120, 180, 240, 300];

function ScenarioCard({ s, onChange, onRemove, selected, onSelect }: {
  s: Scenario; onChange: (s: Scenario) => void; onRemove?: () => void; selected: boolean; onSelect: () => void;
}) {
  const { ws } = useWorkspace();
  const [amountText, setAmountText] = useState(String(s.amount));
  useEffect(() => setAmountText(String(s.amount)), [s.amount]);
  const pay = monthlyPayment(s.amount, s.ratePct, s.termMonths);
  const interest = totalInterest(s.amount, s.ratePct, s.termMonths);
  const ceiling = rateCeilingPct(s.amount, PRIME.pct);
  const uplift = ws.profile.expectedMonthlyUplift?.value;
  const refi = ws.profile.refinanceMonthlyPayment?.value;

  return (
    <article className={`card scen ${selected ? 'glow' : ''}`}>
      <div className="head">
        <input aria-label="Scenario name" value={s.name} maxLength={40} onChange={(e) => onChange({ ...s, name: e.target.value })} />
        {onRemove && <button className="link-btn small" type="button" onClick={onRemove}>Remove</button>}
      </div>

      <div className="field">
        <label htmlFor={`amt-${s.id}`}>Amount</label>
        <input id={`amt-${s.id}`} className="input" inputMode="decimal" value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
          onBlur={() => { const p = parseAmount(amountText); if (p && p.value > 0) onChange({ ...s, amount: p.value }); else setAmountText(String(s.amount)); }} />
        <span className="hint">{fmtMoney(s.amount)}</span>
      </div>

      <div className="slider-row">
        <div className="top"><label htmlFor={`term-${s.id}`}>Repayment length</label><strong>{s.termMonths / 12} years</strong></div>
        <input id={`term-${s.id}`} type="range" min={0} max={TERMS.length - 1} step={1}
          value={Math.max(0, TERMS.indexOf(s.termMonths))}
          aria-valuetext={`${s.termMonths / 12} years`}
          onChange={(e) => onChange({ ...s, termMonths: TERMS[Number(e.target.value)]! })} />
      </div>

      <div className="slider-row">
        <div className="top"><label htmlFor={`rate-${s.id}`}>Interest rate</label><strong>{s.ratePct.toFixed(2)}%</strong></div>
        <input id={`rate-${s.id}`} type="range" min={PRIME.pct} max={ceiling} step={0.25}
          value={Math.min(ceiling, Math.max(PRIME.pct, s.ratePct))}
          aria-valuetext={`${s.ratePct.toFixed(2)} percent`}
          onChange={(e) => onChange({ ...s, ratePct: Number(e.target.value) })} />
        <span className="hint">From prime ({PRIME.pct}%) to the SBA maximum for this amount ({ceiling.toFixed(2)}%)</span>
      </div>

      <div className="out">
        <dl className="kv">
          <dt>Monthly payment</dt><dd>{fmtMoney(pay)}</dd>
          <dt>Total interest</dt><dd>{fmtMoney(interest)}</dd>
          <dt>Total repaid</dt><dd>{fmtMoney(s.amount + interest)}</dd>
          {refi !== undefined && <><dt>vs. your current payment</dt><dd>{pay <= refi ? `${fmtMoney(refi - pay)} less` : `${fmtMoney(pay - refi)} more`} a month</dd></>}
          {uplift !== undefined && <><dt>Your expected extra profit</dt><dd>{fmtMoney(uplift)}/mo</dd></>}
        </dl>
        {uplift !== undefined && (
          <p className={`callout small ${uplift >= pay ? 'good' : 'warn'}`} style={{ marginTop: 10 }}>
            {uplift >= pay
              ? 'On your own estimate, the extra profit covers this payment.'
              : `Your expected extra profit is ${fmtMoney(pay - uplift)} a month short of this payment.`}
          </p>
        )}
        <button className="btn small ghost" type="button" style={{ marginTop: 10 }} onClick={onSelect} aria-pressed={selected}>
          {selected ? 'Checking cash flow for this' : 'Check cash flow for this'}
        </button>
      </div>
    </article>
  );
}

export function Scenarios() {
  const { ws, update } = useWorkspace();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const amount = ws.profile.amount?.value;
  const term = ws.profile.termMonths?.value ?? 120;

  useEffect(() => {
    if (ws.scenarios.length > 0 || !amount) return;
    const rate = defaultRatePct(ws);
    const shorter = term > 84 ? 84 : 60;
    update((w) => ({
      ...w,
      scenarios: [
        { id: newId('s'), name: 'Your request', amount, termMonths: TERMS.includes(term) ? term : 120, ratePct: rate },
        { id: newId('s'), name: `Shorter: ${shorter / 12} years`, amount, termMonths: shorter, ratePct: rate },
        { id: newId('s'), name: 'Borrow 20% less', amount: Math.round(amount * 0.8), termMonths: TERMS.includes(term) ? term : 120, ratePct: rate },
      ],
    }));
  }, [amount, term, ws.scenarios.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!amount && ws.scenarios.length === 0) {
    return (
      <>
        <PageHead eyebrow="Scenarios" title="Compare loan options" />
        <div className="card empty">
          <h3>Start with an amount</h3>
          <p>Tell the assistant roughly how much you need, and we&rsquo;ll set up options to compare.</p>
          <a className="btn primary" href="#/assistant">Open the assistant</a>
        </div>
      </>
    );
  }

  const selected = ws.scenarios.find((s) => s.id === selectedId) ?? ws.scenarios[0];
  const setScenario = (s: Scenario) => update((w) => ({ ...w, scenarios: w.scenarios.map((x) => (x.id === s.id ? s : x)) }));
  const cov = selected ? coverage(ws, selected.amount, selected.termMonths, selected.ratePct) : null;
  const planning = ws.profile.stage?.value === 'planning';

  return (
    <>
      <PageHead eyebrow="Scenarios" title="Compare loan options">
        See how amount, length and rate change your payment and total cost — before you talk to a lender.
      </PageHead>
      <div className="stack-lg">
        <div className="scen-grid">
          {ws.scenarios.map((s) => (
            <ScenarioCard key={s.id} s={s} selected={selected?.id === s.id} onSelect={() => setSelectedId(s.id)}
              onChange={setScenario}
              {...(ws.scenarios.length > 1 ? { onRemove: () => update((w) => ({ ...w, scenarios: w.scenarios.filter((x) => x.id !== s.id) })) } : {})} />
          ))}
        </div>
        {ws.scenarios.length < 3 && selected && (
          <button className="btn" type="button" onClick={() => update((w) => ({ ...w, scenarios: [...w.scenarios, { ...selected, id: newId('s'), name: 'Another option' }] }))}>
            Add an option
          </button>
        )}

        {selected && cov && !planning && (
          <section className="card">
            <div className="card-title"><h2>Does income cover the payments? <span className="faint" style={{ fontWeight: 500 }}>— {selected.name}</span></h2></div>
            <p className="small muted">
              <Term id="dscr">Debt-service coverage</Term> compares what the business earns with all its loan payments.
              It&rsquo;s only calculated from figures you&rsquo;ve confirmed, so it can be relied on.
            </p>
            {cov.ready ? (
              <div className="grid-2">
                <div>
                  <div className={`cov-ratio ${cov.verdict}`}>{cov.ratio}×</div>
                  <p><strong>{cov.verdictLabel}.</strong></p>
                  <dl className="kv">
                    <dt>Income available for loans</dt><dd>{fmtMoney(cov.income!.value)}/yr</dd>
                    <dt>This loan&rsquo;s payments</dt><dd>{fmtMoney(cov.newAnnualPayment!)}/yr</dd>
                    <dt>Existing loan payments</dt><dd>{fmtMoney(cov.existingDebtService?.value ?? 0)}/yr</dd>
                  </dl>
                  <p className="small faint" style={{ marginTop: 8 }}>Income: {cov.income!.derived ?? cov.income!.source}</p>
                </div>
                <div>
                  <h3>Assumptions</h3>
                  <ul className="small muted" style={{ paddingLeft: '1.1em' }}>{cov.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
                  <p className="small muted">Lenders calculate this their own way and commonly want comfortably more than 1× — ask what they look for.</p>
                </div>
              </div>
            ) : (
              <div className="callout info">
                <p>To check this, we need {cov.needs.join(' and ')}.</p>
                <a className="btn small" href="#/documents">Confirm your figures</a>
              </div>
            )}
          </section>
        )}
        {planning && (
          <p className="callout info">For a business that isn&rsquo;t operating yet, lenders look at your projections instead of past income. Upload your plan on the Documents page.</p>
        )}
      </div>
    </>
  );
}
